/**
 * R232q86zbg — Player vs world geometry.
 *
 * The original collision system (Collisions.ts) is a registry of XZ-circles
 * that buildings push themselves into.  WMO walls (loaded by WmoLoader.ts)
 * and ADT terrain (AdtTerrain.ts) never registered into that list, so the
 * player walked through Abbey walls / Stormwind WMOs / etc., and stayed at
 * `groundedY = 0` regardless of real terrain height.
 *
 * This module gives the World render-tick two cheap helpers:
 *
 *   sampleGroundY(scene, x, z, currentY)
 *     Cast a ray downward from (x, currentY+SAMPLE_LIFT, z) and return the
 *     highest terrain/floor hit Y.  Picks ADT tiles, world-ground, WMO floor
 *     surfaces (any mesh with checkCollisions === true).  Used to set the
 *     player's grounded altitude so they stand on Northshire's hills, on the
 *     Abbey floor when inside, etc.
 *
 *   resolvePlayerVsWorld(scene, pos, radius)
 *     Sphere-vs-AABB push-back against every mesh with checkCollisions=true.
 *     Cheap: AABB filter + 2D circle-vs-rectangle horizontal overlap.  Used
 *     to keep the player out of WMO walls.  Pairs with the existing
 *     resolveCollision() from Collisions.ts which still handles registered
 *     hand-tuned circles (fences, hardcoded village blockers).
 *
 * Performance: scene.meshes can hold ~8 000 entries on Elwynn.  We rebuild
 * a small "collidable cache" on demand (every 0.5 s wallclock) of meshes
 * whose `checkCollisions` flag is true AND whose AABB is close to the
 * player.  All math is single-precision Float32 and only walks the
 * reduced cache, not the full scene.
 */

import type { AbstractMesh, Scene } from '@babylonjs/core';
import { Ray, Vector3 } from '@babylonjs/core';

const SAMPLE_LIFT = 6;          // ray starts 6 m above current player y
let wideProbeAt = 0;            // last wall-ray-ring timestamp (throttle)
const SAMPLE_FALL = 600;         // ray length downwards (covers tallest mountain)
const REBUILD_MS = 500;          // refresh nearby-collidables list twice / s
const NEARBY_RADIUS = 60;        // metres around player worth considering

interface CachedMesh {
  mesh: AbstractMesh;
  // Cached world-space AABB (Y-axis kept for ground sampling, XZ for walls).
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

let cache: CachedMesh[] = [];
let cacheBuiltAt = -Infinity;
let cacheCenterX = NaN;
let cacheCenterZ = NaN;

/** Rebuild the nearby-collidables cache when the player has moved >25 m or
 *  REBUILD_MS has elapsed.  Iterates scene.meshes once — O(n). */
function ensureCache(scene: Scene, px: number, pz: number): void {
  const now = performance.now();
  const moved = Math.hypot(px - cacheCenterX, pz - cacheCenterZ) > 25;
  if (!moved && now - cacheBuiltAt < REBUILD_MS && cache.length > 0) return;
  cacheBuiltAt = now;
  cacheCenterX = px;
  cacheCenterZ = pz;
  const next: CachedMesh[] = [];
  const r = NEARBY_RADIUS;
  for (const m of scene.meshes) {
    if (!m) continue;
    // Only meshes flagged for collision count.  WmoLoader.ts sets this on
    // every WMO surface; AdtTerrain meshes are added by name below.
    const flag = (m as { checkCollisions?: boolean }).checkCollisions;
    const nm = m.name || '';
    const isTerrainByName =
      nm.startsWith('adt-') || nm.startsWith('ground-') || nm.startsWith('terrain') || nm === 'world-ground';
    if (!flag && !isTerrainByName) continue;
    if (!m.isEnabled?.()) continue;
    // Use the world-space bounding box.  computeWorldMatrix is already
    // up to date for static meshes; freezeWorldMatrix locks it for WMO.
    let bb;
    try {
      bb = m.getBoundingInfo().boundingBox;
    } catch {
      continue;
    }
    const min = bb.minimumWorld;
    const max = bb.maximumWorld;
    // Reject meshes that are far in XZ even by the broadest test.
    if (max.x < px - r || min.x > px + r) continue;
    if (max.z < pz - r || min.z > pz + r) continue;
    // Skip tiny props (grass, flowers, dropped twigs) — < 0.4 m horizontal.
    // They flag checkCollisions because NaturePropLoader sets it
    // unconditionally; we filter here to keep walking through grass tufts.
    const dx = max.x - min.x;
    const dz = max.z - min.z;
    if (dx < 0.4 && dz < 0.4 && !isTerrainByName) continue;
    next.push({
      mesh: m,
      minX: min.x, maxX: max.x,
      minY: min.y, maxY: max.y,
      minZ: min.z, maxZ: max.z,
    });
  }
  cache = next;
}

/**
 * Vertical ray-cast against terrain + WMO floors.  Returns the y of the
 * highest hit at (x, z), or null if nothing was hit (e.g. off-map).  Caller
 * may fall back to its previous grounded value in that case.
 *
 * `world-ground` is a 7000×3500 m mesh with ~112k triangles — far too
 * expensive to triangle-test every frame.  We skip it here and let the
 * caller's default (groundedY=0) cover that case; ADT tiles + WMO floors
 * are the only meshes that need real sampling.
 *
 * Strategy: shoot the ray from currentY + SAMPLE_LIFT_LOCAL.  If nothing
 * is hit (player below terrain, fell through floor, or just-spawned at
 * y=0 on a +37m ADT tile), retry from SAMPLE_LIFT_HIGH so we can still
 * find the surface.  Two-pass keeps the common case (walking on slope)
 * cheap while still catching teleport / spawn / cliff edge cases.
 */
const SAMPLE_LIFT_LOCAL = SAMPLE_LIFT;       // 6 m above current y
const SAMPLE_LIFT_HIGH  = 600;               // tall fallback for teleports
export function sampleGroundY(scene: Scene, x: number, z: number, currentY: number): number | null {
  ensureCache(scene, x, z);
  const dir = new Vector3(0, -1, 0);
  // Pass 1 — local ray from just above the player.  Cheap, catches the
  // common case where player is on or just above the ground.
  const localOrigin = new Vector3(x, currentY + SAMPLE_LIFT_LOCAL, z);
  const localRay = new Ray(localOrigin, dir, SAMPLE_FALL);
  let bestY: number | null = null;
  // Pre-pass — quickly find ADT mesh (terrain) at this XZ; if it covers,
  // and we're not inside a building, use it directly.  ADT.intersects
  // costs ~32k tris but Babylon's per-mesh AABB filter walks subMeshes
  // first so the actual triangle test is bounded.
  let testedHeavy = 0;
  for (const c of cache) {
    const nm = c.mesh.name || '';
    if (nm === 'world-ground') continue;
    if (x < c.minX || x > c.maxX) continue;
    if (z < c.minZ || z > c.maxZ) continue;
    if (c.minY > localOrigin.y) continue;
    if (c.maxY < localOrigin.y - SAMPLE_FALL) continue;
    // Cap heavy triangle tests at 8 meshes per call — beyond that we likely
    // already have a representative hit and the rest are roof beams /
    // exterior walls / decor inside a WMO.
    if (testedHeavy >= 8 && bestY !== null) break;
    testedHeavy++;
    const info = c.mesh.intersects(localRay, false);
    if (info?.hit && info.pickedPoint) {
      const y = info.pickedPoint.y;
      if (bestY === null || y > bestY) bestY = y;
    }
  }
  if (bestY !== null) return bestY;
  // Pass 2 — fallback: player below terrain.  Shoot from sky downward.
  // This catches spawn at y=0 on a +37 m ADT tile, or any teleport that
  // dropped the player below mesh surfaces.  More expensive (no AABB-Y
  // filter possible) but only runs when pass 1 missed.
  const highOrigin = new Vector3(x, currentY + SAMPLE_LIFT_HIGH, z);
  const highRay = new Ray(highOrigin, dir, SAMPLE_LIFT_HIGH + SAMPLE_FALL);
  for (const c of cache) {
    const nm = c.mesh.name || '';
    if (nm === 'world-ground') continue;
    if (x < c.minX || x > c.maxX) continue;
    if (z < c.minZ || z > c.maxZ) continue;
    if (c.maxY < highOrigin.y - (SAMPLE_LIFT_HIGH + SAMPLE_FALL)) continue;
    const info = c.mesh.intersects(highRay, false);
    if (info?.hit && info.pickedPoint) {
      const y = info.pickedPoint.y;
      if (bestY === null || y > bestY) bestY = y;
    }
  }
  return bestY;
}

/**
 * Horizontal sphere vs world geometry.  Walks the nearby cache; any mesh
 * whose XZ-AABB intersects the player's circle pushes the player out along
 * the shortest axis.  `pos` is mutated in-place; returns true if any hit
 * was applied.
 *
 * Player ellipsoid is approximated as an upright cylinder of `radius` m
 * with vertical extent `[pos.y, pos.y + height]`; meshes that lie entirely
 * above or below that band don't block (e.g. roof beams 4 m up).
 */
export function resolvePlayerVsWorld(
  scene: Scene,
  pos: { x: number; y: number; z: number },
  radius: number,
  height: number,
): boolean {
  ensureCache(scene, pos.x, pos.z);
  let any = false;
  // Vertical band the player occupies.
  const pMinY = pos.y + 0.1;
  const pMaxY = pos.y + height;
  // Two-pass to settle stacked overlaps without runaway oscillation.
  for (let pass = 0; pass < 2; pass++) {
    let hit = false;
    for (const c of cache) {
      // Skip terrain — it must not push the player horizontally; only WMO
      // walls / doodads.  Terrain XZ AABB covers a 533 m tile and would
      // shove the player at every step.
      const nm = c.mesh.name || '';
      if (nm.startsWith('adt-') || nm.startsWith('ground-') || nm === 'world-ground') continue;
      // Vertical band overlap — mesh must intersect the player's column.
      if (c.maxY < pMinY) continue;
      if (c.minY > pMaxY) continue;
      // Closest point on AABB (XZ) to player centre.
      const cx = Math.max(c.minX, Math.min(pos.x, c.maxX));
      const cz = Math.max(c.minZ, Math.min(pos.z, c.maxZ));
      const dx = pos.x - cx;
      const dz = pos.z - cz;
      const dsq = dx * dx + dz * dz;
      if (dsq >= radius * radius) continue;
      // R232q86zg9: merged WMO buildings have building-sized AABBs — pushing
      // out of the whole box made an «invisible wall» metres from the real
      // stone («спотыкаюсь об воздух»). For any wide mesh, probe the actual
      // geometry with a short horizontal ray ring and push only on real
      // wall hits (doorways stay walkable).
      const wide = (c.maxX - c.minX) > 8 || (c.maxZ - c.minZ) > 8;
      if (wide) {
        // Rays against a merged building are pricey — throttle to ~11 Hz.
        // Walls are thick; at 7 m/s the 90 ms gap is ≤0.65 m overshoot,
        // which the next ring corrects before any visual clipping.
        const tNow = (typeof performance !== 'undefined' ? performance.now() : Date.now());
        if (tNow - wideProbeAt < 90) continue;
        wideProbeAt = tNow;
        const chest = new Vector3(pos.x, pos.y + 0.95, pos.z);
        const reach = radius + 0.30;
        for (let k = 0; k < 6; k++) {
          const a = (k / 6) * Math.PI * 2;
          const dirX = Math.sin(a), dirZ = Math.cos(a);
          const ray = new Ray(chest, new Vector3(dirX, 0, dirZ), reach);
          const info = c.mesh.intersects(ray, false);
          if (info?.hit && info.pickedPoint) {
            const hitD = Math.hypot(info.pickedPoint.x - chest.x, info.pickedPoint.z - chest.z);
            const depth = reach - hitD;
            if (depth > 0.001) {
              pos.x -= dirX * depth;
              pos.z -= dirZ * depth;
              hit = true;
              any = true;
            }
          }
        }
        continue;
      }
      // We're inside the AABB-grow.  Push out along the shortest axis.
      if (dsq < 1e-6) {
        // Player centre exactly inside AABB — push along the smallest
        // distance to a face.
        const left   = pos.x - c.minX;
        const right  = c.maxX - pos.x;
        const back   = pos.z - c.minZ;
        const front  = c.maxZ - pos.z;
        const m = Math.min(left, right, back, front);
        if (m === left)  pos.x = c.minX - radius;
        else if (m === right) pos.x = c.maxX + radius;
        else if (m === back)  pos.z = c.minZ - radius;
        else                  pos.z = c.maxZ + radius;
      } else {
        const d = Math.sqrt(dsq);
        const push = (radius - d) / d;
        pos.x += dx * push;
        pos.z += dz * push;
      }
      hit = true;
      any = true;
    }
    if (!hit) break;
  }
  return any;
}

/** Force a cache rebuild on the next call.  Useful after large scene
 *  changes (zone load, dispose). */
export function invalidatePlayerCollisionCache(): void {
  cache = [];
  cacheBuiltAt = -Infinity;
  cacheCenterX = NaN;
  cacheCenterZ = NaN;
}
